/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef } from "react";
import { Room, RoomEvent, Track } from "livekit-client";
import { useStream } from "../context/StreamProvider";
import { useAppDispatch, useAppSelector } from "../utils/hooks";
import { socketInstance } from "../socket/socket";
import { updatePeerData } from "../store/Slices/PeerJsSlice";
import {
  addConnectedUser,
  removeConnectedUser,
} from "../store/Slices/ConnectedUserSlice";
import { CallEndedRes, UserJoinedRes } from "../types/ResType";
import axiosInstance from "../store/api/axiosInstance";
import { focusLocalAsMainIfPrivate, focusRemoteAsMainIfPrivate } from "../utils/privateCallMainVideo";
import {
  beginCallTeardown,
  idlePeerCallPatch,
  leaveCallToChat,
} from "../utils/endCallSession";

function livekitSyntheticPeerId(userId: number) {
  return `livekit-${userId}`;
}

function buildMediaStreamFromPublications(room: Room) {
  const ms = new MediaStream();
  room.localParticipant.getTrackPublications().forEach((pub) => {
    const t = pub.track;
    if (t?.mediaStreamTrack) ms.addTrack(t.mediaStreamTrack);
  });
  return ms;
}

/**
 * WebRTC через LiveKit, когда в sessionStorage media_engine === "livekit".
 * Токен звонящего — в sessionStorage (ответ make-call); принимающий — POST /api/call/livekit-token.
 */
export function useLiveKit(enabled: boolean) {
  const { addUserStream, removeUserStream, updateUserStream, updateCurrentStream } =
    useStream();
  const { isVideoEnabled, isAudioEnabled, call_id, chat_id } = useAppSelector(
    (s) => s.PeerJsSlice,
  );
  const userData = useAppSelector((s) => s.userData);
  const dispatch = useAppDispatch();
  const socket = socketInstance();
  const roomRef = useRef<Room | null>(null);
  const remoteStreamsRef = useRef<Record<string, MediaStream>>({});

  const dispose = () => {
    roomRef.current?.disconnect(true).catch(() => {});
    roomRef.current = null;
    remoteStreamsRef.current = {};
    sessionStorage.removeItem("livekit_token");
    if (!beginCallTeardown()) return;
    dispatch(updatePeerData(idlePeerCallPatch()));
    leaveCallToChat();
  };

  useEffect(() => {
    if (!enabled || !userData.user_id || !call_id || !chat_id) return;

    let cancelled = false;
    const call_type = sessionStorage.getItem("call_type");
    const isVideo = call_type === "video";
    const localPeerId = String(userData.user_id);

    sessionStorage.setItem("livekit_peer_id", livekitSyntheticPeerId(userData.user_id));

    const onUserJoined = (userjoinData: UserJoinedRes) => {
      dispatch(
        addConnectedUser({
          profile_image: userjoinData.user.profile_pic,
          user_id: userjoinData.user.user_id,
          full_name: userjoinData.user.full_name,
          user_name: userjoinData.user.user_name,
        }),
      );
    };

    const onUserLeft = (user_left_data: UserJoinedRes) => {
      const uid = user_left_data?.user?.user_id;
      if (uid != null) removeUserStream(String(uid));
      dispatch(removeConnectedUser(user_left_data?.user.user_id));
      focusLocalAsMainIfPrivate(localPeerId, updateCurrentStream);
    };

    const onCallEnded = (_data: CallEndedRes) => {
      dispose();
    };

    const onCallChanges = (data: any) => {
      const pid = data?.peer_id;
      if (!pid) return;
      updateUserStream(pid, {
        isAudioEnabled:
          data.isAudioEnabled !== undefined ? data.isAudioEnabled : true,
        isVideoEnabled:
          data.isVideoEnabled !== undefined ? data.isVideoEnabled : true,
      });
    };

    const attachRemoteTrack = (
      track: { kind: Track.Kind; mediaStreamTrack: MediaStreamTrack },
      participant: { identity: string; name?: string },
    ) => {
      if (track.kind !== Track.Kind.Audio && track.kind !== Track.Kind.Video)
        return;
      const pid = participant.identity;
      if (pid !== localPeerId && !sessionStorage.getItem("callStartTime")) {
        sessionStorage.setItem("callStartTime", Date.now().toString());
      }
      let stream = remoteStreamsRef.current[pid];
      if (!stream) {
        stream = new MediaStream();
        remoteStreamsRef.current[pid] = stream;
        const uid = Number(pid);
        addUserStream({
          peerId: pid,
          userId: Number.isFinite(uid) ? uid : 0,
          userName: participant.name || `User ${pid}`,
          stream,
          isLocal: false,
          isAudioEnabled: true,
          isVideoEnabled: isVideo,
          currentVideo: false,
          isScreenSharing: false,
        });
        focusRemoteAsMainIfPrivate(pid, updateCurrentStream);
      }
      const existing = stream.getTracks().some(
        (t) => t.kind === track.mediaStreamTrack.kind,
      );
      if (!existing) stream.addTrack(track.mediaStreamTrack);
      updateUserStream(pid, {
        stream: new MediaStream(stream.getTracks()),
        isVideoEnabled: isVideo,
      });
    };

    (async () => {
      try {
        let url = sessionStorage.getItem("livekit_url") || "";
        let token = sessionStorage.getItem("livekit_token") || "";
        const isCaller = sessionStorage.getItem("is_caller") === "true";

        if (!isCaller || !token) {
          const res = await axiosInstance.post("call/livekit-token", {
            call_id,
          });
          if (cancelled) return;
          token = res.data?.token ?? "";
          if (res.data?.url) url = res.data.url;
          if (url) sessionStorage.setItem("livekit_url", url);
        }

        if (!url || !token) {
          console.error("[LiveKit] Нет url или token");
          return;
        }

        const room = new Room({ adaptiveStream: true, dynacast: true });
        roomRef.current = room;

        await room.connect(url, token);
        if (cancelled) {
          await room.disconnect(true);
          return;
        }

        // Audio call: microphone only. Video call: mic + camera.
        await room.localParticipant.setMicrophoneEnabled(true);
        if (isVideo) {
          await room.localParticipant.setCameraEnabled(true);
        } else {
          await room.localParticipant.setCameraEnabled(false);
        }

        // Снятие браузерной блокировки воспроизведения удалённого аудио (частично помогает сразу после getUserMedia).
        await room.startAudio().catch(() => {
          /* Safari и др. — жест пользователя; см. unlockRoomAudio в VideoCall */
        });

        const localStream = buildMediaStreamFromPublications(room);
        addUserStream({
          peerId: localPeerId,
          userName: "You",
          userId: userData.user_id,
          stream: localStream,
          isAudioEnabled,
          isVideoEnabled: isVideo,
          isLocal: true,
          currentVideo: true,
          isScreenSharing: false,
        });

        dispatch(
          updatePeerData({
            peer_id: sessionStorage.getItem("livekit_peer_id") || "",
            isVideoEnabled: isVideo,
            isAudioEnabled: true,
          }),
        );

        // Caller is already in current_users from make-call. Emitting accept_call
        // here marked the DB ongoing before B answered and fired
        // call_accepted_elsewhere on the caller's other devices.
        if (!isCaller) {
          socket.emit("accept_call", {
            call_id,
            peer_id: sessionStorage.getItem("livekit_peer_id"),
            chat_id,
            user_name: userData.user_name,
            user_id: userData.user_id,
          });
        }

        room.on(RoomEvent.TrackSubscribed, (track, _pub, participant) => {
          if (participant.identity === localPeerId) return;
          attachRemoteTrack(track, participant);
        });

        room.on(RoomEvent.LocalTrackPublished, () => {
          const ms = buildMediaStreamFromPublications(room);
          updateUserStream(localPeerId, { stream: ms });
        });

        room.on(RoomEvent.ParticipantDisconnected, (participant) => {
          const pid = participant.identity;
          delete remoteStreamsRef.current[pid];
          removeUserStream(pid);
          const uid = Number(pid);
          if (Number.isFinite(uid)) dispatch(removeConnectedUser(uid));
          focusLocalAsMainIfPrivate(localPeerId, updateCurrentStream);
        });

        room.on(RoomEvent.Disconnected, () => {
          roomRef.current = null;
        });

        room.remoteParticipants.forEach((participant) => {
          participant.trackPublications.forEach((pub) => {
            if (pub.isSubscribed && pub.track)
              attachRemoteTrack(pub.track, participant);
          });
        });

        socket.on("user_joined", onUserJoined);
        socket.on("user_left", onUserLeft);
        socket.on("call_ended", onCallEnded);
        socket.on("call_changes", onCallChanges);
      } catch (e) {
        console.error("[LiveKit]", e);
      }
    })();

    return () => {
      cancelled = true;
      socket.off("user_joined", onUserJoined);
      socket.off("user_left", onUserLeft);
      socket.off("call_ended", onCallEnded);
      socket.off("call_changes", onCallChanges);
      roomRef.current?.disconnect(true).catch(() => {});
      roomRef.current = null;
    };
  }, [enabled, userData.user_id, call_id, chat_id]);

  useEffect(() => {
    if (!enabled || !roomRef.current) return;
    const callType = sessionStorage.getItem("call_type");
    // Never request/enable camera on audio calls.
    const shouldEnableCamera = callType === "video" && isVideoEnabled;
    roomRef.current.localParticipant
      .setCameraEnabled(shouldEnableCamera)
      .catch(() => {});
  }, [enabled, isVideoEnabled]);

  useEffect(() => {
    if (!enabled || !roomRef.current) return;
    roomRef.current.localParticipant
      .setMicrophoneEnabled(isAudioEnabled)
      .catch(() => {});
  }, [enabled, isAudioEnabled]);

  const unlockRoomAudio = useCallback(async () => {
    const r = roomRef.current;
    if (!r) return;
    await r.startAudio().catch(() => {});
  }, []);

  return { roomRef, dispose, unlockRoomAudio };
}
